import { LoadingSkeleton } from "./LoadingSkeleton";
import { useEffect, useRef, useState } from "react";
import type { AdminGateway, ContentDetails } from "../domain";
import { useLocale } from "../i18n";

function ReviewVideo({ url, onRead }: { url: string; onRead: () => void }) {
  const ref = useRef<HTMLVideoElement>(null);
  const [failed, setFailed] = useState(false);
  const { w } = useLocale();
  useEffect(() => {
    const video = ref.current;
    if (!video) return;
    let disposed = false;
    let player: import("hls.js").default | undefined;
    if (url.includes(".m3u8") && !video.canPlayType("application/vnd.apple.mpegurl")) {
      void import("hls.js").then(({ default: Hls }) => {
        if (disposed) return;
        if (!Hls.isSupported()) { setFailed(true); return; }
        player = new Hls({ enableWorker: false });
        player.loadSource(url);
        player.attachMedia(video);
        player.on(Hls.Events.ERROR, (_, data) => { if (data.fatal) setFailed(true); });
      }).catch(() => setFailed(true));
    } else video.src = url;
    return () => { disposed = true; player?.destroy(); };
  }, [url]);
  return <><video ref={ref} controls playsInline preload="metadata" onEnded={onRead} onError={() => setFailed(true)} />
    {failed && <p role="alert">{w("تعذر تشغيل الوسيط؛ لا يمكن اعتماد المحتوى قبل فحصه.", "Playback failed; inspect the media before approving.")}</p>}</>;
}

export function ContentInspection({ id, gateway, onInspected }: { id: string; gateway: AdminGateway; onInspected: (value: boolean, version: number) => void }) {
  const { w } = useLocale();
  const [details, setDetails] = useState<ContentDetails | null>(null);
  const [seen, setSeen] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  function record(mediaId: string) { setSeen(previous => new Set([...previous, mediaId])); }
  async function open() {
    setLoading(true); setError(false);
    try { setDetails(await gateway.details!(id)); } catch { setError(true); }
    finally { setLoading(false); }
  }
  return <div className="content-inspection">
    {!details && <button className="secondary" disabled={loading} onClick={() => void open()}>{loading ? <LoadingSkeleton variant="inline" label={w("جارٍ التحميل…", "Loading…")} /> : w("فتح المحتوى الكامل للمراجعة", "Inspect full content")}</button>}
    {error && <p role="alert">{w("تعذر تحميل المحتوى. حاول مجدداً.", "Could not load content. Try again.")}</p>}
    {details && <>
      <p>{details.text}</p>
      {details.media.map(media => {
        const video = media.contentType?.startsWith("video/") || !!media.playbackUrl;
        const url = video ? media.playbackUrl || media.url : media.url;
        if (!url || !url.startsWith("https://") || media.ready === false) return <p key={media.id}>{w("الوسيط غير جاهز للمراجعة.", "Media is not ready for review.")}</p>;
        return video ? <ReviewVideo key={media.id} url={url} onRead={() => record(media.id)} /> :
          <img key={media.id} src={url} alt={w("صورة المنشور", "Post image")} onLoad={() => record(media.id)} referrerPolicy="no-referrer" />;
      })}
      <label><input type="checkbox" disabled={!details.canReview || details.media.some(media => !seen.has(media.id))}
        onChange={event => { onInspected(event.target.checked, details.version); }} />{w("راجعت النص وكافة الوسائط كاملة", "I reviewed the text and all media in full")}</label>
      {!details.canReview && <p>{w("لا يمكنك مراجعة هذا المحتوى، أو تم اتخاذ القرار مسبقاً.", "You cannot review this content, or it was already decided.")}</p>}
    </>}
  </div>;
}
